import React, { useState, useEffect } from 'react';
import {
    BarChartOutlined,
    LoginOutlined, MessageOutlined, PieChartOutlined,
    RobotOutlined, TeamOutlined, TruckOutlined, WhatsAppOutlined
} from '@ant-design/icons';
import { Avatar, Badge, ConfigProvider, Dropdown, Layout, Menu, Space, Spin, theme } from 'antd';
import { useAuth } from './contexts/AuthContext'; // Updated import
import { useWatiChat } from './contexts/WatiChatContext';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { Tooltip } from '@mui/material';
import useSnackbar from "../components/Snackbar/UseSnackbar";
import useLogout from './Login/Logout';

const { Header, Content, Footer, Sider } = Layout;

function getItem(label, key, icon, path) {
    return { key, icon, label: <Link to={path}>{label}</Link>, path, title: label };
}

const DrawerNew = () => {
    const { handledialogueOpen, LogoutDialog } = useLogout();
    const { SuccessSnackbar, ErrorSnackbar, InfoSnackbar, WarningSnackbar, ToastContainer } = useSnackbar();

    // Use the new AuthContext hook
    const { authState, legacyAuthState } = useAuth();
    const { totalUnread } = useWatiChat();
    const location = useLocation();
    const navigate = useNavigate();

    // For backward compatibility, use legacyAuthState if available
    const userState = legacyAuthState || authState;

    function stringAvatar(name) {
        if (!name) return "UU";
        if (name.split(' ').length === 2) {
            return `${name.split(' ')[0][0]}${name.split(' ')[1][0]}`;
        } else {
            return `${name.split(' ')[0][0].toUpperCase()}`;
        }
    }

    // Define menu items - use isAuthenticated from new authState
    const items = authState.isAuthenticated
        ? [
            getItem('Dashboard', '/dashboard', <PieChartOutlined />, '/dashboard'),
            getItem('Queryset V3', '/queryset', <WhatsAppOutlined />, '/queryset'),
            getItem('Bot Status', '/WhatsApp', <RobotOutlined />, '/WhatsApp'),
            getItem('WhatsApp Messages', '/whatsapp_messages', <Badge count={totalUnread} size="small" offset={[10, 10]}><MessageOutlined /></Badge>, '/whatsapp_messages'),
            getItem('SRM', '/suppliers', <TruckOutlined />, '/suppliers'),
            getItem('HRM', '/hrm', <TeamOutlined />, '/hrm'),
            getItem('Reports', '/reports', <BarChartOutlined />, '/reports'),
        ]
        : [];

    const [collapsed, setCollapsed] = useState(false);
    const [selectedKey, setSelectedKey] = useState('');
    const [currentHeader, setCurrentHeader] = useState('');
    const [isLoading, setIsLoading] = useState(false); // Start as false since auth is handled by App

    // Remove the auth checking useEffect - this is now handled by App.js
    useEffect(() => {
        setTimeout(() => {
            setCollapsed(true);
        }, 2500);
    }, []);

    // Set menu and header based on current location
    useEffect(() => {
        if (!authState.isAuthenticated) return;

        const setInitialMenuState = () => {
            // Find exact match first
            let matchedItem = items.find(item =>
                location.pathname === item.path
            );

            // If no exact match, try to match path prefix
            if (!matchedItem) {
                matchedItem = items.find(item =>
                    location.pathname.startsWith(item.path)
                );
            }

            // Special case for reports
            if (!matchedItem && location.pathname.startsWith('/report')) {
                matchedItem = items.find(item => item.path === '/reports');
            }

            if (matchedItem) {
                setSelectedKey(matchedItem.key);
                setCurrentHeader(matchedItem.title);
            } else {
                setSelectedKey('');
                setCurrentHeader('Welcome');
            }
        };

        setInitialMenuState();
    }, [location.pathname, items, authState.isAuthenticated]);

    const {
        token: { colorBgContainer, borderRadiusLG },
    } = theme.useToken();

    // Don't show loading spinner here - auth is handled by App
    if (!authState.isAuthenticated) {
        return null; // Or a redirect component
    }

    const dropdown_items = [
        {
            key: '1',
            label: "Profile",
        },
        {
            key: '2',
            label: "Reset Password",
        },
        {
            key: '3',
            label: "Logout",
        },
    ];

    const onMenuClick = (e) => {
        switch (e.key) {
            case "1":
                InfoSnackbar(`Keep calm, coffee is being converted to code...`);
                break;
            case "2":
                InfoSnackbar(`Keep calm, coffee is being converted to code...`);
                break;
            case "3":
                handledialogueOpen();
                break;
            default:
                ErrorSnackbar(`Invalid option selected!`);
                break;
        }
    };

    return (
        <ConfigProvider
            theme={{
                algorithm: theme.defaultAlgorithm,
            }}
        >
            <Layout style={{ minHeight: '100vh', minWidth: '100vw' }}>
                <Sider
                    collapsible
                    collapsed={collapsed}
                    onCollapse={(value) => setCollapsed(value)}
                    style={{
                        overflow: "auto",
                        height: "100vh",
                        position: "sticky",
                        top: 0,
                        left: 0
                    }}
                >
                    <div className="demo-logo-vertical" />
                    <Menu
                        theme="dark"
                        selectedKeys={[selectedKey]}
                        mode="inline"
                        items={items}
                    />
                </Sider>
                <Layout>
                    <div className='flex justify-center sticky top-0 left-0 z-99 drop-shadow'>
                        <Header
                            className='flex justify-between border items-center drop-shadow mb-3'
                            style={{
                                borderRadius: "12px",
                                padding: 10,
                                background: colorBgContainer,
                                width: "99.5%",
                                backgroundColor: "#03224B",
                                color: "white"
                            }}
                        >
                            <h1 className="ml-5">{currentHeader}</h1>
                            <Dropdown
                                className='cursor-pointer'
                                menu={{
                                    items: dropdown_items,
                                    onClick: (e) => onMenuClick(e)
                                }}
                                placement="bottomLeft"
                            >
                                <Tooltip placement='left' title={`Logged in as ${userState?.user?.name || "Unknown User"}`}>
                                    <Avatar
                                        size={"large"}
                                        className='mr-3'
                                        style={{ backgroundColor: '#fde3cf', color: '#f56a00' }}
                                    >
                                        {stringAvatar(userState?.user?.name || "Unknown User")}
                                    </Avatar>
                                </Tooltip>
                            </Dropdown>
                        </Header>
                    </div>
                    <Content style={{ margin: '0 16px' }}>
                        <div
                            style={{
                                padding: 24,
                                minHeight: 360,
                                background: colorBgContainer,
                                borderRadius: borderRadiusLG,
                            }}
                        >
                            <Outlet />
                        </div>
                    </Content>
                    <Footer style={{ textAlign: 'center' }}>
                        UzdPart Used Spare Parts Trading LLC © {new Date().getFullYear()} SRM v4.6.0 build 13012026
                    </Footer>
                </Layout>
            </Layout>
            <LogoutDialog />
            <ToastContainer />
        </ConfigProvider>
    );
};

export default DrawerNew;